import {useTranslation} from 'react-i18next';
import {device} from '@guestapp/ui/src/utils/device';
import ChekinProSettings from '../../ChekinProSettings';
import {useSummary} from 'context/summary';
import {useFetchLockConditions, useScreenResize} from 'hooks';
import {useChekinSDK} from 'context/ChekinSDK';
import {useKioskoStore} from 'context/kiosko/useKioskoStore';
import {AUTH_INIT_MODELS, OFFER_STATUSES} from '@guestapp/sdk';
import BookingDatesBox from 'components/BookingDatesBox';
import CheckInInfoBox from 'components/CheckInInfoBox';
import RemoteAccessNavigationItem from 'components/RemoteAccessNavigationItem';
import HomeViewModals from 'components/HomeViewModals';
import GuidebooksNavigationList from 'components/GuidebooksNavigationList';
import {GigSkyBannerDesktop} from 'components/GigSkyBanner/GigSkyBannerDesktop';
import {GigSkyBannerMobile} from 'components/GigSkyBanner/GigSkyBannerMobile';
import {GuruwalkWidget} from 'components/GuruWalk/GuruwalkWidget';
import {HomeUpsellingList} from 'components/upselling/HomeUpsellingList/HomeUpsellingList';
import {Mozio} from 'components/upselling/Mozio';
import {EndSessionButton} from 'components/sessions/EndSessionButton';
import {
  StyledBox,
  Wrapper,
  BookingDatesBoxContainer,
  CheckinInfoContainer,
  StyledPage,
  ContentItemElement,
} from './styled';
import {ToggleFeatures} from 'utils/features/ToggleFeatures/ToggleFeatures';
import {AiraloWidget} from 'components/airalo/AiraloWidget/AiraloWidget';

function HomeView() {
  const {t} = useTranslation();
  const {isLoading, data: summary} = useSummary();
  const {isMatch: isMobileMode} = useScreenResize(device.laptop);
  const {modelName} = useChekinSDK();
  const isKiosko = useKioskoStore(state => state.connection.isConnected);
  const isUniversalLink = modelName === AUTH_INIT_MODELS.housing;

  const {data: lockConditions} = useFetchLockConditions({
    enabled: Boolean(summary?.is_remote_access_enabled),
  });

  const showGigSkyBanner =
    summary?.gigsky_status === OFFER_STATUSES.active && !ChekinProSettings.isSDK;

  const showMozio =
    summary?.mozio_status === OFFER_STATUSES.active && !ChekinProSettings.isSDK;

  const showDesktopBanner = !isMobileMode && showGigSkyBanner;

  if (isUniversalLink) {
    return null;
  }

  return (
    <div>
      {showDesktopBanner && !isKiosko && <GigSkyBannerDesktop />}
      <StyledPage
        mainHeader={isMobileMode}
        title={t('confirm_booking_details')}
        isLoading={isLoading}
        className="home-view"
      >
        <Wrapper $hasBanner={showDesktopBanner} className="home-view__wrapper">
          <CheckinInfoContainer className="chekin-boxes__container">
            <StyledBox className="chekin-boxes__tasks-container">
              <CheckInInfoBox />
            </StyledBox>
            <BookingDatesBoxContainer>
              <BookingDatesBox />
            </BookingDatesBoxContainer>
          </CheckinInfoContainer>
          {isMobileMode /*|| isKiosko*/ && showGigSkyBanner && <GigSkyBannerMobile />}
        </Wrapper>

        {(isKiosko ||
          (summary?.is_remote_access_enabled &&
            lockConditions?.is_show_keys_enabled)) && (
          <ContentItemElement>
            <RemoteAccessNavigationItem />
          </ContentItemElement>
        )}

        {showMozio && (
          <ContentItemElement>
            <Mozio housingId={summary.id} />
          </ContentItemElement>
        )}

        {summary?.guidebooks?.active && (
          <ContentItemElement>
            <GuidebooksNavigationList />
          </ContentItemElement>
        )}

        {summary?.upselling?.active && (
          <ContentItemElement>
            <HomeUpsellingList />
          </ContentItemElement>
        )}

        {summary?.guruwalk_status === OFFER_STATUSES.active && summary.location && (
          <GuruwalkWidget housingId={summary.id} location={summary.location} />
        )}

        <ToggleFeatures
          feature="UP_397_AIRALO_ENABLED"
          on={
            <ContentItemElement>
              <AiraloWidget />
            </ContentItemElement>
          }
        />

        <HomeViewModals />
        <EndSessionButton />
      </StyledPage>
    </div>
  );
}

export {HomeView};
